import { useEffect, useRef, useState } from "react";
import { formatRcd } from "../overlay/format";
import { t } from "../strings";
import { useGen1Nav } from "./useGen1Nav";

export interface MarketplacePokemon {
  id: string;
  name: string;
  image: string;
  details: readonly string[];
}

export interface MarketplaceListing extends MarketplacePokemon {
  /** Whole ₽ as a decimal integer string (or number in legacy preview fixtures). */
  priceRcd: string | number;
  kind: "pokemon" | "item";
  seller: string;
  listingId?: string;
}

type View =
  | { id: "browse" }
  | { id: "product"; listingId: string }
  | { id: "owned" }
  | { id: "price" | "review"; pokemonId: string };

const NO_POKEMON: readonly MarketplacePokemon[] = [];

function ListingImage({ listing }: { listing: MarketplacePokemon }) {
  const [failed, setFailed] = useState(false);
  return failed || !listing.image ? (
    <span
      className="wallet-market__image-missing"
      role="img"
      aria-label={listing.name}
    >
      ?
    </span>
  ) : (
    <img src={listing.image} alt="" onError={() => setFailed(true)} />
  );
}

function Cards({
  entries,
  index,
  caption,
  onPick,
  onFocus,
}: {
  entries: readonly MarketplacePokemon[];
  index: number;
  caption: (index: number) => string;
  onPick: (index: number) => void;
  onFocus: (index: number) => void;
}) {
  return (
    <ul
      className="wallet-market__grid"
      role="listbox"
      aria-label="Pokémon and items"
    >
      {entries.map((entry, i) => (
        <li key={entry.id} role="presentation">
          <button
            type="button"
            className="wallet-market__card"
            role="option"
            aria-selected={index === i}
            aria-label={`${entry.name}, ${caption(i)}`}
            onFocus={() => onFocus(i)}
            onClick={() => onPick(i)}
          >
            <span className="wallet-market__cursor" aria-hidden="true">
              {index === i ? "▶" : "\u00a0"}
            </span>
            <span className="wallet-market__picture">
              <ListingImage
                key={`${entry.id}:${entry.image}`}
                listing={entry}
              />
            </span>
            <span className="wallet-market__name">{entry.name}</span>
            <span className="wallet-market__cost">{caption(i)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function Actions({
  labels,
  index,
  onPick,
  onFocus,
}: {
  labels: readonly string[];
  index: number;
  onPick: (index: number) => void;
  onFocus: (index: number) => void;
}) {
  return (
    <div className="wallet-market__actions" aria-label="Menu actions">
      {labels.map((label, i) => (
        <button
          key={label}
          type="button"
          onFocus={() => onFocus(i)}
          onClick={() => onPick(i)}
        >
          <span aria-hidden="true">{index === i ? "▶" : "\u00a0"}</span>
          {label}
        </button>
      ))}
    </div>
  );
}

function Hero({
  entry,
  heading,
  seller,
  compact = false,
}: {
  entry: MarketplacePokemon;
  heading?: string;
  seller?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`wallet-market__hero${compact ? " wallet-market__hero--compact" : ""}`}
    >
      <div className="wallet-market__portrait">
        <ListingImage key={`${entry.id}:${entry.image}`} listing={entry} />
      </div>
      <div className="wallet-market__meta">
        {heading && <p>{heading}</p>}
        {entry.details.map((detail) => (
          <p key={detail}>{detail}</p>
        ))}
        {seller && <p>{t.walletMarketSeller(seller)}</p>}
      </div>
    </div>
  );
}

function pageActions(page: number, count: number) {
  return [
    ...(page > 0 ? ["PREV"] : []),
    ...(page + 1 < count ? ["NEXT"] : []),
    "BACK",
  ];
}

/** Marketplace design flow only. BUY and LIST do not submit transactions. */
export function Marketplace({
  listings,
  ownedPokemon = NO_POKEMON,
  onBack,
  onBuy,
  onList,
}: {
  listings: readonly MarketplaceListing[];
  ownedPokemon?: readonly MarketplacePokemon[];
  onBack: () => void;
  onBuy?: (listing: MarketplaceListing) => Promise<void> | void;
  onList?: (pokemon: MarketplacePokemon, priceRcd: string) => Promise<void> | void;
}) {
  const rootRef = useRef<HTMLElement>(null);
  const [view, setView] = useState<View>({ id: "browse" });
  const [requestedPage, setPage] = useState(0);
  const [requestedOwnedPage, setOwnedPage] = useState(0);
  const [notice, setNotice] = useState(false);
  const [price, setPrice] = useState("");
  const [priceError, setPriceError] = useState(false);
  const pageCount = Math.max(1, Math.ceil(listings.length / 2));
  const page = Math.min(requestedPage, pageCount - 1);
  const visible = listings.slice(page * 2, page * 2 + 2);
  const actions = pageActions(page, pageCount);
  const ownedPageCount = Math.max(1, Math.ceil(ownedPokemon.length / 2));
  const ownedPage = Math.min(requestedOwnedPage, ownedPageCount - 1);
  const ownedVisible = ownedPokemon.slice(ownedPage * 2, ownedPage * 2 + 2);
  const ownedActions = pageActions(ownedPage, ownedPageCount);
  const selected =
    view.id === "product"
      ? listings.find((entry) => entry.id === view.listingId)
      : undefined;
  const editor =
    view.id === "price" || view.id === "review"
      ? ownedPokemon.find((entry) => entry.id === view.pokemonId)
      : undefined;
  const goBrowse = () => setView({ id: "browse" });
  const goOwned = () => setView({ id: "owned" });

  const pick = (index: number) => {
    const listing = visible[index];
    if (listing) {
      setNotice(false);
      setView({ id: "product", listingId: listing.id });
    } else if (index === visible.length) goOwned();
    else {
      const action = actions[index - visible.length - 1];
      if (action === "BACK") onBack();
      else if (action === "PREV" || action === "NEXT") {
        setPage(page + (action === "PREV" ? -1 : 1));
        browseNav.setIndex(0);
      }
    }
  };
  const browseNav = useGen1Nav(
    view.id === "browse" ? visible.length + actions.length + 1 : 0,
    pick,
    onBack,
  );
  const productPick = (index: number) => {
    if (index !== 0) {
      goBrowse();
      return;
    }
    if (selected && onBuy) {
      setNotice(true);
      void Promise.resolve(onBuy(selected)).catch(() => setNotice(true));
    } else setNotice(true);
  };
  const detailNav = useGen1Nav(
    view.id === "product" ? 2 : 0,
    productPick,
    goBrowse,
  );
  const ownedPick = (index: number) => {
    const pokemon = ownedVisible[index];
    if (pokemon) {
      setPrice("");
      setPriceError(false);
      setNotice(false);
      setView({ id: "price", pokemonId: pokemon.id });
    } else {
      const action = ownedActions[index - ownedVisible.length];
      if (action === "BACK") goBrowse();
      else if (action === "PREV" || action === "NEXT") {
        setOwnedPage(ownedPage + (action === "PREV" ? -1 : 1));
        ownedNav.setIndex(0);
      }
    }
  };
  const ownedNav = useGen1Nav(
    view.id === "owned" ? ownedVisible.length + ownedActions.length : 0,
    ownedPick,
    goBrowse,
  );
  const review = () => {
    if (!editor || !/^\d+$/.test(price) || BigInt(price) <= 0n) {
      setPriceError(true);
      return;
    }
    setPriceError(false);
    setNotice(false);
    setView({ id: "review", pokemonId: editor.id });
  };
  const editPrice = () => {
    setNotice(false);
    if (editor) setView({ id: "price", pokemonId: editor.id });
    else goOwned();
  };
  const pricePick = (index: number) => (index === 0 ? review() : goOwned());
  const priceNav = useGen1Nav(view.id === "price" ? 2 : 0, pricePick, goOwned);
  const reviewPick = (index: number) => {
    if (index !== 0) {
      editPrice();
      return;
    }
    if (editor && onList) {
      setNotice(true);
      void Promise.resolve(onList(editor, price)).catch(() => setNotice(true));
    } else setNotice(true);
  };
  const reviewNav = useGen1Nav(
    view.id === "review" ? 2 : 0,
    reviewPick,
    editPrice,
  );

  useEffect(() => {
    if (view.id === "product" && !selected) setView({ id: "browse" });
    if ((view.id === "price" || view.id === "review") && !editor)
      setView({ id: "owned" });
  }, [view, selected, editor]);

  useEffect(() => {
    // Keep DOM focus and the game cursor together after paging or changing views.
    const first =
      rootRef.current?.querySelector<HTMLElement>("input") ??
      rootRef.current?.querySelector<HTMLElement>('[role="option"]') ??
      rootRef.current?.querySelector<HTMLElement>(
        ".wallet-market__actions button",
      );
    first?.focus({ preventScroll: true });
  }, [view.id, page, ownedPage]);

  const title =
    view.id === "browse"
      ? "MARKET"
      : view.id === "owned"
        ? "YOUR POKéMON"
        : view.id === "price"
          ? "SET PRICE"
          : view.id === "review"
            ? "CONFIRM LIST"
            : (selected?.name ?? "MARKET");
  const browsing = view.id === "browse";
  const choosing = view.id === "owned";

  return (
    <section
      ref={rootRef}
      className="wallet-market"
      aria-label={browsing ? "Marketplace" : title}
    >
      <header className="wallet-market__heading">
        <h2 className="wallet-menu__title">{title}</h2>
        {(browsing || choosing) && (
          <span
            aria-label={`Page ${(choosing ? ownedPage : page) + 1} of ${choosing ? ownedPageCount : pageCount}`}
          >
            {(choosing ? ownedPage : page) + 1}/
            {choosing ? ownedPageCount : pageCount}
          </span>
        )}
        {browsing && (
          <button
            type="button"
            className="wallet-market__list-button"
            onFocus={() => browseNav.setIndex(visible.length)}
            onClick={goOwned}
          >
            <span aria-hidden="true">
              {browseNav.index === visible.length ? "▶" : "\u00a0"}
            </span>
            LIST
          </button>
        )}
      </header>
      {browsing || choosing ? (
        <>
          {(choosing ? ownedVisible : visible).length === 0 ? (
            <p className="wallet-market__empty">
              {choosing ? t.walletMarketNothingToList : t.walletMarketEmpty}
            </p>
          ) : (
            <Cards
              entries={choosing ? ownedVisible : visible}
              index={choosing ? ownedNav.index : browseNav.index}
              caption={(index) =>
                choosing
                  ? (ownedVisible[index]!.details[0] ?? "POKéMON")
                  : formatRcd(String(visible[index]!.priceRcd))
              }
              onPick={choosing ? ownedPick : pick}
              onFocus={choosing ? ownedNav.setIndex : browseNav.setIndex}
            />
          )}
          <Actions
            labels={choosing ? ownedActions : actions}
            index={
              choosing
                ? ownedNav.index - ownedVisible.length
                : browseNav.index - visible.length - 1
            }
            onFocus={(index) =>
              choosing
                ? ownedNav.setIndex(ownedVisible.length + index)
                : browseNav.setIndex(visible.length + 1 + index)
            }
            onPick={(index) =>
              choosing
                ? ownedPick(ownedVisible.length + index)
                : pick(visible.length + 1 + index)
            }
          />
        </>
      ) : selected ? (
        <>
          <div
            className="wallet-market__product"
            role="region"
            aria-label="Listing details"
            tabIndex={0}
          >
            <Hero
              entry={selected}
              heading={
                notice
                  ? t.walletMarketBuySoon
                  : selected.kind === "pokemon"
                    ? "POKéMON"
                    : "ITEM"
              }
              seller={selected.seller}
            />
            <dl className="wallet-menu__kv wallet-market__price">
              <dt>Price</dt>
              <dd>{formatRcd(selected.priceRcd)}</dd>
            </dl>
            {notice && (
              <span className="wallet-market__announcement" role="status">
                {t.walletMarketBuySoon}
              </span>
            )}
          </div>
          <Actions
            labels={["BUY", "BACK"]}
            index={detailNav.index}
            onFocus={detailNav.setIndex}
            onPick={productPick}
          />
        </>
      ) : editor ? (
        <>
          <div
            className={`wallet-market__product${priceError ? " wallet-market__product--invalid" : ""}`}
            role="region"
            aria-label="Your listing"
            tabIndex={0}
          >
            <Hero entry={editor} heading={editor.name} compact />
            {view.id === "price" ? (
              <label className="wallet-market__amount">
                PRICE (RCD)
                <input
                  aria-label="Listing price in RCD"
                  inputMode="numeric"
                  value={price}
                  maxLength={16}
                  aria-invalid={priceError}
                  aria-describedby={
                    priceError ? "market-price-error" : undefined
                  }
                  onChange={(event) => {
                    setPrice(event.target.value.replace(/\D/g, ""));
                    setPriceError(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace") event.stopPropagation();
                    if (event.key === "Enter") {
                      event.preventDefault();
                      event.stopPropagation();
                      review();
                    }
                  }}
                />
              </label>
            ) : (
              <dl className="wallet-menu__kv wallet-market__price">
                <dt>Price</dt>
                <dd>{formatRcd(Number(price))}</dd>
              </dl>
            )}
            {priceError && (
              <p
                id="market-price-error"
                role="alert"
                className="wallet-market__notice"
              >
                {t.walletMarketPriceError}
              </p>
            )}
            {notice && (
              <p role="status" className="wallet-market__notice">
                {t.walletMarketListSoon}
              </p>
            )}
          </div>
          <Actions
            labels={view.id === "price" ? ["REVIEW", "BACK"] : ["LIST", "BACK"]}
            index={view.id === "price" ? priceNav.index : reviewNav.index}
            onFocus={
              view.id === "price" ? priceNav.setIndex : reviewNav.setIndex
            }
            onPick={view.id === "price" ? pricePick : reviewPick}
          />
        </>
      ) : null}
    </section>
  );
}
